/**
 * Day -> [Branch ->] User -> Waiter -> Payment Mode tree builder for the Session Sales page,
 * built from getUserWaiterSalesHalfDayReport's per-(user, waiter, date, branch) rows. Shared
 * with (extracted from) the Sales Reports page, which used to carry a "Session Sales" tab
 * built from the exact same rows.
 */
import moment from 'moment';

export const WAITER_EMPTY_HALF = { total_sales: '0.00', bill_count: 0, payment_modes: {} };

export const emptyWaiterAgg = () => ({ total_sales: 0, bill_count: 0, payment_modes: {} });

export const addWaiterInto = (acc, half) => {
    const h = half || WAITER_EMPTY_HALF;
    acc.total_sales += parseFloat(h.total_sales || 0);
    acc.bill_count += Number(h.bill_count || 0);
    Object.entries(h.payment_modes || {}).forEach(([mode, amt]) => {
        acc.payment_modes[mode] = (acc.payment_modes[mode] || 0) + parseFloat(amt || 0);
    });
};

export const WAITER_LEVEL_COLORS = { day: 'var(--primary-color, #2a78d6)', branch: '#7c3aed', user: '#2a78d6', waiter: '#1baf7a', mode: '#eda100' };
export const WAITER_LEVEL_ICONS = { day: 'pi-calendar', branch: 'pi-building', user: 'pi-user', waiter: 'pi-users', mode: 'pi-wallet' };

// Day -> [Branch ->] User -> Waiter -> Payment Mode tree scaffold. buildUserChildren is a seam
// left in place for what to nest under each user — currently only buildWaiterChildren below.
export const buildWaiterSessionTree = (rows, buildUserChildren, formatDateFn) => {
    if (!rows.length) return [];

    const leafData = (label, level, first, second, total) => ({ label, level, first_half: first, second_half: second, total });

    const aggRows = (dataRows) => {
        const agg = { first_half: emptyWaiterAgg(), second_half: emptyWaiterAgg(), total: emptyWaiterAgg() };
        dataRows.forEach((r) => {
            addWaiterInto(agg.first_half, r.first_half);
            addWaiterInto(agg.second_half, r.second_half);
            addWaiterInto(agg.total, r.total);
        });
        return agg;
    };

    const buildUserNodes = (dataRows, keyPrefix) => {
        const rowsByUser = {};
        dataRows.forEach((r) => {
            (rowsByUser[r.user_id] ||= { username: r.username, rows: [] }).rows.push(r);
        });

        const userKeys = Object.keys(rowsByUser).sort((a, b) => String(rowsByUser[a].username || '').localeCompare(String(rowsByUser[b].username || '')));

        return userKeys.map((userId) => {
            const { username, rows: userRows } = rowsByUser[userId];
            const userAgg = aggRows(userRows);
            const userKeyPrefix = `${keyPrefix}-u-${userId}`;
            return {
                key: userKeyPrefix,
                data: leafData(username, 'user', userAgg.first_half, userAgg.second_half, userAgg.total),
                children: buildUserChildren(userRows, userKeyPrefix, leafData)
            };
        });
    };

    const showBranchLevel = new Set(rows.map((r) => r.branch_id ?? '')).size > 1;

    const rowsByDate = {};
    rows.forEach((r) => {
        (rowsByDate[r.payment_date] ||= []).push(r);
    });

    const dates = Object.keys(rowsByDate).sort((a, b) => {
        const da = moment(a, ['DD-MM-YYYY', 'YYYY-MM-DD'], true);
        const db = moment(b, ['DD-MM-YYYY', 'YYYY-MM-DD'], true);
        return db.valueOf() - da.valueOf();
    });

    return dates.map((date) => {
        const dayRows = rowsByDate[date] || [];
        const dayAgg = aggRows(dayRows);

        let children;
        if (showBranchLevel) {
            const rowsByBranch = {};
            dayRows.forEach((r) => {
                const branchKey = r.branch_id ?? '';
                (rowsByBranch[branchKey] ||= { branchName: r.branch_name || branchKey || 'Unknown Branch', rows: [] }).rows.push(r);
            });
            const branchKeys = Object.keys(rowsByBranch).sort((a, b) => String(rowsByBranch[a].branchName).localeCompare(String(rowsByBranch[b].branchName)));

            children = branchKeys.map((branchKey) => {
                const { branchName, rows: branchRows } = rowsByBranch[branchKey];
                const branchAgg = aggRows(branchRows);
                const branchKeyPrefix = `${date}-b-${branchKey}`;
                return {
                    key: branchKeyPrefix,
                    data: leafData(branchName, 'branch', branchAgg.first_half, branchAgg.second_half, branchAgg.total),
                    children: buildUserNodes(branchRows, branchKeyPrefix)
                };
            });
        } else {
            children = buildUserNodes(dayRows, date);
        }

        return {
            key: date,
            data: leafData(formatDateFn(date), 'day', dayAgg.first_half, dayAgg.second_half, dayAgg.total),
            children
        };
    });
};

// waiter_no 0/null/'' is the sentinel for "no waiter tagged" (billed straight to the cashier's
// own login) — not a real waiter (see BillReportsContent's getBilledByName/billedByBodyTemplate,
// which uses the same convention).
const hasNoWaiter = (waiterNo) => waiterNo === null || waiterNo === undefined || String(waiterNo).trim() === '' || Number(waiterNo) === 0;

// User -> Waiter -> Payment Mode (each waiter's bills are billed under a specific cashier
// login, so this keeps that drill-down level). A row with no waiter tagged (waiter_no 0) isn't
// a distinct waiter — skip the redundant Waiter row and attach its payment modes straight under
// the User instead.
export const buildWaiterChildren = (userRows, keyPrefix, leafData) =>
    userRows
        .slice()
        .sort((a, b) => String(a.waiter_name || '').localeCompare(String(b.waiter_name || '')))
        .flatMap((r, i) => {
            const modeNames = Array.from(
                new Set([...Object.keys(r.first_half?.payment_modes || {}), ...Object.keys(r.second_half?.payment_modes || {}), ...Object.keys(r.total?.payment_modes || {})])
            ).sort();
            const modeNodes = modeNames.map((mode) => ({
                key: `${keyPrefix}-${r.waiter_no}-m-${mode}-${i}`,
                data: leafData(
                    mode,
                    'mode',
                    { total_sales: r.first_half?.payment_modes?.[mode] || 0 },
                    { total_sales: r.second_half?.payment_modes?.[mode] || 0 },
                    { total_sales: r.total?.payment_modes?.[mode] || 0 }
                )
            }));
            if (hasNoWaiter(r.waiter_no)) return modeNodes;
            return [
                {
                    key: `${keyPrefix}-w-${r.waiter_no}-${i}`,
                    data: leafData(r.waiter_name, 'waiter', r.first_half || WAITER_EMPTY_HALF, r.second_half || WAITER_EMPTY_HALF, r.total || WAITER_EMPTY_HALF),
                    children: modeNodes
                }
            ];
        });
